import React, { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import Layout from "../components/Layout";
import { api, API, formatBRL, formatDateBR, formatDateTimeBR, maskPhone, STATUS_LABELS, RISK_LABELS, DOC_TYPE_LABELS, REQUIRED_DOCS } from "../lib/api";
import { useAuth } from "../lib/auth";
import { StatusBadge, RiskBadge } from "../components/Badges";
import {
  ArrowLeft, Copy, Save, CheckCircle2, XCircle, FileWarning,
  FileText, MessageSquare, ShieldAlert, ExternalLink, Loader2,
  MessageCircle, IdCard, Download, Upload, FileSignature, MapPin, Trash2, Plus,
  Lock, Unlock,
} from "lucide-react";
import { toast } from "sonner";
import ContractSection from "../components/ContractSection";
import ContractsListSection from "../components/ContractsListSection";
import AIAnalysisSection from "../components/AIAnalysisSection";
import InstallmentContractsSection from "../components/InstallmentContractsSection";
import DocumentSlots, { DocCard } from "../components/DocumentSlots";
import SendWhatsAppModal from "../components/SendWhatsAppModal";
import ClientCommunicationCard from "../components/ClientCommunicationCard";
import ChangeIndicatorModal from "../components/ChangeIndicatorModal";
import { UserCog } from "lucide-react";

function BlockSection({ client, canBlock, canUnblock, onChanged }) {
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const blocked = !!client?.is_blocked;

  const block = async () => {
    const r = reason.trim();
    if (!r) { toast.error("Informe o motivo do bloqueio"); return; }
    setBusy(true);
    try {
      await api.post(`/clients/${client.id}/block`, { reason: r });
      toast.success("Cliente bloqueado");
      setReason("");
      onChanged?.();
    } catch (err) {
      const d = err?.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "Erro ao bloquear");
    } finally { setBusy(false); }
  };
  const unblock = async () => {
    if (!window.confirm("Desbloquear este cliente? A ação será registrada no histórico.")) return;
    setBusy(true);
    try {
      await api.post(`/clients/${client.id}/unblock`);
      toast.success("Cliente desbloqueado");
      onChanged?.();
    } catch (err) {
      const d = err?.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "Erro ao desbloquear");
    } finally { setBusy(false); }
  };

  const history = client?.block_history || [];

  return (
    <section className={`fc-card p-5 sm:p-6 ${blocked ? "border-red-500/30" : ""}`} data-testid="block-section">
      <div className="flex items-center justify-between mb-3">
        <h2 className="font-display text-[15px] font-semibold tracking-tight flex items-center gap-2">
          {blocked ? <Lock size={15} className="text-red-400"/> : <Unlock size={15} className="text-[#737373]"/>}
          Bloqueio do cliente
          {blocked && <span className="badge-base bg-red-500/15 border-red-500/30 text-red-300 text-[10px] ml-1"><Lock size={9}/> Bloqueado</span>}
        </h2>
      </div>

      {blocked ? (
        <div className="space-y-3">
          <div className="text-[12.5px] text-[#A3A3A3]">
            <div><span className="text-[#737373]">Motivo:</span> <span className="text-white">{client.block_reason}</span></div>
            <div className="mt-1"><span className="text-[#737373]">Bloqueado em:</span> {formatDateTimeBR(client.blocked_at)} · por {client.blocked_by_email}</div>
          </div>
          {canUnblock ? (
            <button onClick={unblock} disabled={busy} className="fc-btn-ghost text-[12px] py-2 px-3 flex items-center gap-1.5" data-testid="btn-unblock-client">
              {busy ? <Loader2 size={12} className="animate-spin"/> : <Unlock size={12}/>} Desbloquear
            </button>
          ) : (
            <div className="text-[11.5px] text-[#737373]">Apenas administradores podem desbloquear.</div>
          )}
        </div>
      ) : canBlock ? (
        <div className="space-y-2">
          <textarea
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            rows={2}
            maxLength={500}
            placeholder="Motivo do bloqueio (obrigatório)"
            className="fc-input resize-none text-[13px]"
            data-testid="block-reason-input"
          />
          <button
            onClick={block}
            disabled={busy || !reason.trim()}
            className="text-[12px] px-3 py-2 rounded-md border border-red-500/40 bg-red-500/10 text-red-300 hover:bg-red-500/20 disabled:opacity-40 flex items-center gap-1.5"
            data-testid="btn-block-client"
          >
            {busy ? <Loader2 size={12} className="animate-spin"/> : <Lock size={12}/>} Bloquear cliente
          </button>
          <div className="text-[11px] text-[#737373]">Cliente bloqueado não cria, renova ou recebe contrato.</div>
        </div>
      ) : null}

      {history.length > 0 && (
        <details className="mt-4 text-[12px] text-[#A3A3A3]">
          <summary className="cursor-pointer text-[11.5px] uppercase tracking-wider text-[#737373]">Histórico ({history.length})</summary>
          <ul className="mt-2 space-y-2">
            {history.slice().reverse().map((h, i) => (
              <li key={i} className="flex gap-2 items-start">
                <div className={`w-1.5 h-1.5 rounded-full mt-1.5 flex-shrink-0 ${h.action === "block" ? "bg-red-400" : "bg-emerald-400"}`}/>
                <div className="min-w-0">
                  <div className="text-white">
                    {h.action === "block" ? "Bloqueado" : "Desbloqueado"} por {h.by}
                  </div>
                  <div className="text-[11px] text-[#737373]">{formatDateTimeBR(h.at)}</div>
                  {h.reason && <div className="text-[12px] text-[#A3A3A3] mt-0.5">Motivo: {h.reason}</div>}
                </div>
              </li>
            ))}
          </ul>
        </details>
      )}
    </section>
  );
}

function Section({ title, children, right }) {
  return (
    <section className="fc-card p-6 sm:p-7">
      <div className="flex items-center justify-between mb-5">
        <h2 className="font-display text-[17px] font-semibold tracking-tight">{title}</h2>
        {right}
      </div>
      {children}
    </section>
  );
}

function Info({ label, value, mono }) {
  return (
    <div>
      <div className="text-[11px] uppercase tracking-wider text-[#737373]">{label}</div>
      <div className={`mt-1 text-[14px] text-white ${mono ? "font-mono-tab" : ""}`}>{value ?? "—"}</div>
    </div>
  );
}

export default function ClientDetailPage() {
  const { id } = useParams();
  const { user } = useAuth();
  const nav = useNavigate();
  const [c, setC] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({});
  const [preview, setPreview] = useState(null); // {url, type, name}
  const [waOpen, setWaOpen] = useState(false);
  const [changeIndOpen, setChangeIndOpen] = useState(false);
  const isIndicador = user?.role === "indicador";
  const canEdit = user?.role === "admin" || user?.role === "gestora";
  const canDelete = user?.role === "admin";
  const canSendWhatsApp = user?.role === "admin" || user?.role === "gestora" || user?.role === "socio";

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/clients/${id}`);
      setC(data);
      setForm({
        status: data.status || "novo",
        risk_level: data.risk_level || "",
        approved_amount: data.approved_amount ?? "",
        installments: data.installments ?? "",
        installment_amount: data.installment_amount ?? "",
        first_due_date: data.first_due_date || "",
        internal_notes: data.internal_notes || "",
        analysis_justification: data.analysis_justification || "",
        next_action: data.next_action || "",
      });
    } catch (err) {
      toast.error("Cliente não encontrado");
      nav("/clientes");
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [id]);

  const save = async (override = {}) => {
    setSaving(true);
    try {
      const payload = { ...form, ...override };
      ["approved_amount", "installments", "installment_amount"].forEach(k => {
        if (payload[k] === "" || payload[k] === null) payload[k] = null;
        else if (payload[k] !== undefined) payload[k] = Number(payload[k]);
      });
      Object.keys(payload).forEach(k => { if (payload[k] === "") payload[k] = null; });
      const { data } = await api.patch(`/clients/${id}`, payload);
      setC(prev => ({ ...prev, ...data }));
      toast.success("Análise salva");
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Erro ao salvar");
    } finally { setSaving(false); }
  };

  const quickAction = async (status) => {
    await save({ status });
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text).then(() => toast.success("Copiado!"));
  };

  const commissionAmount = c?.commission_amount ?? ((c?.approved_amount || 0) * ((c?.commission_percent ?? 5) / 100));

  // Status-aware WhatsApp text for indicator
  const indicatorStatusText = (() => {
    if (!c) return "";
    const nome = c.full_name;
    const valorAprovado = formatBRL(form.approved_amount || c.approved_amount);
    const valorComissao = formatBRL(commissionAmount);
    const justificativa = form.analysis_justification || c.analysis_justification || "";
    const proxAcao = form.next_action || c.next_action || "";
    switch (form.status) {
      case "aprovado":
      case "aprovacao_parcial":
        return `Cliente ${nome} aprovado.\nValor aprovado: ${valorAprovado}.\nSua comissão: ${valorComissao}.`;
      case "negado":
        return `Cliente ${nome} foi negado.${justificativa ? `\nMotivo: ${justificativa}.` : ""}\nObrigado pelo encaminhamento.`;
      case "pendente_documentacao":
        return `Cliente ${nome} está com pendência de documentação.\nDocumentos faltantes/observação: ${proxAcao || "favor verificar com o cliente"}.\nAssim que regularizado, retomamos a análise.`;
      case "contratado":
        return `Cliente ${nome} foi contratado.\nValor: ${valorAprovado}.\nSua comissão: ${valorComissao}.`;
      case "pago":
        return `Cliente ${nome} quitou. Comissão liberada: ${valorComissao}.`;
      default:
        return `Cliente ${nome} — Status: ${STATUS_LABELS[form.status] || form.status}.${proxAcao ? `\n${proxAcao}` : ""}`;
    }
  })();

  const indicatorWhatsHref = c?.indicator_whatsapp || c?.indicator_phone
    ? `https://wa.me/55${String(c.indicator_whatsapp || c.indicator_phone).replace(/\D/g, "")}?text=${encodeURIComponent(indicatorStatusText)}`
    : null;

  const summaryWhats = c ? `Cliente: ${c.full_name}
Indicador: ${c.indicator_name || "—"}
Valor solicitado: ${formatBRL(c.requested_amount)}
Status: ${STATUS_LABELS[form.status] || form.status}
Risco: ${RISK_LABELS[form.risk_level] || "—"}
Observação: ${form.next_action || "—"}` : "";

  const clientText = c ? `Olá, ${c.full_name?.split(" ")[0] || "tudo bem"}. Recebemos seu cadastro e ele está com status: ${STATUS_LABELS[form.status] || form.status}. Nossa equipe irá te chamar para dar sequência.` : "";

  const indicatorText = indicatorStatusText;

  const openDoc = async (doc) => {
    try {
      const res = await api.get(`/documents/${doc.id}/file`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      setPreview({ url, type: doc.content_type, name: doc.original_filename, label: DOC_TYPE_LABELS[doc.document_type] });
    } catch (_) { toast.error("Não foi possível abrir o documento"); }
  };

  const [confirmDelete, setConfirmDelete] = useState(false);
  const [deleteText, setDeleteText] = useState("");
  const [deleting, setDeleting] = useState(false);

  const remove = async () => {
    if (deleteText.trim().toUpperCase() !== "EXCLUIR") return;
    setDeleting(true);
    try {
      await api.delete(`/clients/${id}`);
      // Fecha o modal ANTES de navegar para evitar race do React reconciling
      // o backdrop ao mesmo tempo que troca de rota (causa "insertBefore" no DOM)
      setConfirmDelete(false);
      setDeleteText("");
      setDeleting(false);
      toast.success("Cliente excluído permanentemente");
      // Deixa o React commitar o close do modal antes de navegar
      setTimeout(() => nav("/clientes", { replace: true }), 0);
    } catch (err) {
      const d = err?.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "Erro ao excluir cliente");
      setDeleting(false);
    }
  };

  const closeDelete = () => {
    if (deleting) return;
    setConfirmDelete(false);
    setDeleteText("");
  };

  if (loading || !c) {
    return <Layout><div className="text-[#A3A3A3] py-20 text-center">Carregando...</div></Layout>;
  }

  return (
    <Layout>
      <Link to="/clientes" className="inline-flex items-center gap-1.5 text-[#A3A3A3] hover:text-white text-sm mb-5" data-testid="back-link">
        <ArrowLeft size={14}/> Voltar
      </Link>

      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-4 mb-6">
        <div>
          <div className="text-[11px] uppercase tracking-[0.2em] text-[#D4AF37]">Cliente</div>
          <h1 className="font-display text-3xl sm:text-[34px] font-bold tracking-tight mt-1" data-testid="client-name">{c.full_name}</h1>
          <div className="flex flex-wrap items-center gap-2 mt-2.5">
            <StatusBadge status={form.status} />
            <RiskBadge risk={form.risk_level} />
            <span className="text-[12px] text-[#737373]">Cadastrado em {formatDateBR(c.created_at)}</span>
          </div>
          {!isIndicador && Number(c.approved_amount) > 0 && (
            <div className="mt-3 inline-flex items-center gap-2 px-3 py-1.5 rounded border border-[#D4AF37]/40 bg-[#D4AF37]/10">
              <span className="text-[10.5px] uppercase tracking-wider text-[#D4AF37]">Valor aprovado</span>
              <span className="font-mono-tab text-[15px] text-[#E8C547] font-semibold" data-testid="header-approved-amount">
                {formatBRL(c.approved_amount)}
              </span>
            </div>
          )}
        </div>
        {canSendWhatsApp && (
          <div className="flex-shrink-0">
            <button
              onClick={() => setWaOpen(true)}
              className="fc-btn-primary text-xs flex items-center gap-2"
              data-testid="btn-send-whatsapp"
              title="Enviar mensagem via WhatsApp"
            >
              <MessageSquare size={14}/> Enviar WhatsApp
            </button>
          </div>
        )}
      </div>

      {/* Sticky action bar — sempre visível para Aprovar / Negar / Pendência / Salvar */}
      {canEdit && (
        <div
          className="sticky top-0 z-30 -mx-4 sm:-mx-6 lg:-mx-8 px-4 sm:px-6 lg:px-8 py-2.5 mb-5 border-y border-[#1f1f1f] bg-[#0a0a0a]/85 backdrop-blur supports-[backdrop-filter]:bg-[#0a0a0a]/65"
          data-testid="sticky-action-bar"
        >
          <div className="flex flex-wrap items-center gap-2">
            <span className="text-[10.5px] uppercase tracking-[0.18em] text-[#737373] mr-1 hidden sm:inline">Ações</span>
            <button onClick={() => quickAction("aprovado")} className="fc-btn-ghost flex items-center gap-1.5 text-emerald-300 border-emerald-500/30 hover:border-emerald-500/60 hover:bg-emerald-500/5" data-testid="btn-approve">
              <CheckCircle2 size={15}/> Aprovar
            </button>
            <button onClick={() => quickAction("negado")} className="fc-btn-ghost flex items-center gap-1.5 text-red-300 border-red-500/30 hover:border-red-500/60 hover:bg-red-500/5" data-testid="btn-deny">
              <XCircle size={15}/> Negar
            </button>
            <button onClick={() => quickAction("pendente_documentacao")} className="fc-btn-ghost flex items-center gap-1.5 text-purple-300 border-purple-500/30 hover:border-purple-500/60 hover:bg-purple-500/5" data-testid="btn-pending">
              <FileWarning size={15}/> Pendência
            </button>
            {(form.status === "aprovado" || form.status === "aprovacao_parcial") && (
              <button onClick={() => quickAction("contratado")} className="fc-btn-ghost flex items-center gap-1.5 text-[#D4AF37] border-[#D4AF37]/40 hover:border-[#D4AF37]/70 hover:bg-[#D4AF37]/5" data-testid="btn-contracted">
                <FileSignature size={15}/> Contratado
              </button>
            )}
            {form.status === "contratado" && (
              <button onClick={() => quickAction("pago")} className="fc-btn-ghost flex items-center gap-1.5 text-teal-300 border-teal-500/30 hover:border-teal-500/60 hover:bg-teal-500/5" data-testid="btn-paid">
                <CheckCircle2 size={15}/> Marcar pago
              </button>
            )}
            <div className="ml-auto">
              <button onClick={() => save()} disabled={saving} className="fc-btn-gold flex items-center gap-1.5" data-testid="btn-save">
                {saving ? <Loader2 size={15} className="animate-spin"/> : <Save size={15}/>} Salvar análise
              </button>
            </div>
          </div>
        </div>
      )}

      <div className="grid lg:grid-cols-12 gap-5">
        {/* LEFT COLUMN — Dados / Contatos / Referências / Documentos / Localização */}
        <div className="lg:col-span-4 space-y-5">
          <Section title="Resumo">
            <div className="grid grid-cols-2 md:grid-cols-3 gap-5">
              <Info label="CPF" value={isIndicador ? (c.cpf_masked || "—") : (c.cpf ? c.cpf.replace(/^(\d{3})(\d{3})(\d{3})(\d{2})$/,"$1.$2.$3-$4") : "—")} mono />
              {!isIndicador && <Info label="Nascimento" value={c.birth_date ? formatDateBR(c.birth_date) : "—"} />}
              <Info label="WhatsApp" value={maskPhone(c.whatsapp)} />
              <Info label="Cidade / UF" value={`${c.city || "—"} / ${c.state || "—"}`} />
              <div>
                <div className="text-[10.5px] uppercase tracking-wider text-[#737373]">Indicador</div>
                <div className="mt-1 flex items-center gap-2 flex-wrap">
                  <span className="text-[13px] text-[#E5E5E5]">{c.indicator_name || "—"}</span>
                  {canEdit && (
                    <button
                      onClick={() => setChangeIndOpen(true)}
                      className="text-[11px] px-2 py-0.5 rounded border border-[#D4AF37]/30 text-[#D4AF37] hover:bg-[#D4AF37]/10 flex items-center gap-1"
                      data-testid="btn-change-indicator"
                      title="Trocar indicador com registro auditável"
                    >
                      <UserCog size={11}/> Trocar
                    </button>
                  )}
                </div>
              </div>
              {!isIndicador && <Info label="Renda mensal" value={formatBRL(c.monthly_income)} mono />}
              {!isIndicador && <Info label="Profissão" value={c.profession} />}
              {!isIndicador && <Info label="Empresa" value={c.company_name} />}
              {!isIndicador && <Info label="Tempo de trabalho" value={c.employment_time} />}
              <Info label="Valor solicitado" value={formatBRL(c.requested_amount)} mono />
              <Info label="Valor aprovado" value={formatBRL(form.approved_amount)} mono />
              <Info label="Finalidade" value={c.purpose} />
            </div>
            {(c.commission_amount !== undefined) && (
              <div className="mt-5 p-3.5 rounded-md bg-[#0f0f0f] border border-[#D4AF37]/20 flex items-center justify-between gap-3" data-testid="commission-block">
                <div>
                  <div className="text-[11px] uppercase tracking-wider text-[#737373]">Comissão do indicador ({c.commission_percent || 5}%)</div>
                  <div className="font-display text-xl font-bold text-[#D4AF37] mt-1">{formatBRL(c.commission_amount)}</div>
                </div>
                <div className="text-[11px] text-[#737373] max-w-[260px] text-right">
                  Calculada sobre o valor aprovado. Visível para ADMIN e para o próprio indicador.
                </div>
              </div>
            )}
            <div className="mt-5">
              {!isIndicador && <Info label="Endereço" value={c.address} />}
            </div>
            {c.client_notes && !isIndicador && (
              <div className="mt-5">
                <Info label="Observações do cliente" value={c.client_notes} />
              </div>
            )}
          </Section>

          {/* Referências pessoais — admin/gestora */}
          {!isIndicador && (() => {
            const ref1Ok = Boolean((c.ref1_name || "").trim() && c.ref1_whatsapp);
            const ref2Ok = Boolean((c.ref2_name || "").trim() && c.ref2_whatsapp);
            const printRecebido = (c.documents || []).some((d) => d.document_type === "print_rede_social");
            const allOk = ref1Ok && ref2Ok && printRecebido;
            return (
              <Section
                title="Referências pessoais e rede social"
                right={
                  <span className={`badge-base ${allOk ? "bg-emerald-500/10 border-emerald-500/40 text-emerald-300" : "bg-amber-500/10 border-amber-500/40 text-amber-300"}`}
                    data-testid="refs-status-badge"
                  >
                    {allOk ? "Completo" : "Pendências"}
                  </span>
                }
              >
                <div className="grid sm:grid-cols-2 gap-5">
                  <div className="rounded-md border border-[#262626] p-4" data-testid="ref1-block">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-[11px] uppercase tracking-wider text-[#737373]">Referência 1</span>
                      <span className={`badge-base text-[10px] ${ref1Ok ? "bg-emerald-500/10 border-emerald-500/40 text-emerald-300" : "bg-[#1a1a1a] border-[#2a2a2a] text-[#737373]"}`}>
                        {ref1Ok ? "✓ Recebida" : "Não preenchida"}
                      </span>
                    </div>
                    <div className="text-[13px] text-[#E5E5E5]">{c.ref1_name || "—"}</div>
                    <div className="text-[12px] text-[#A3A3A3]">{c.ref1_parentesco || "—"}</div>
                    <div className="text-[12px] text-[#D4AF37] font-mono-tab">{c.ref1_whatsapp || "—"}</div>
                  </div>
                  <div className="rounded-md border border-[#262626] p-4" data-testid="ref2-block">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-[11px] uppercase tracking-wider text-[#737373]">Referência 2</span>
                      <span className={`badge-base text-[10px] ${ref2Ok ? "bg-emerald-500/10 border-emerald-500/40 text-emerald-300" : "bg-[#1a1a1a] border-[#2a2a2a] text-[#737373]"}`}>
                        {ref2Ok ? "✓ Recebida" : "Não preenchida"}
                      </span>
                    </div>
                    <div className="text-[13px] text-[#E5E5E5]">{c.ref2_name || "—"}</div>
                    <div className="text-[12px] text-[#A3A3A3]">{c.ref2_parentesco || "—"}</div>
                    <div className="text-[12px] text-[#D4AF37] font-mono-tab">{c.ref2_whatsapp || "—"}</div>
                  </div>
                  <div className="sm:col-span-2" data-testid="print-rs-block">
                    <DocCard
                      docType="print_rede_social"
                      doc={(c.documents || []).find(d => d.document_type === "print_rede_social")}
                      clientId={id}
                      canEdit={canEdit}
                      onPreview={openDoc}
                      onChanged={load}
                    />
                  </div>
                </div>
              </Section>
            );
          })()}

          {!isIndicador && (
            <Section
              title="Documentos"
              right={canEdit ? <span className="badge-base bg-[#1A1A1A] border-[#D4AF37]/30 text-[#D4AF37]"><Upload size={11}/> Upload habilitado</span> : null}
            >
              <DocumentSlots
                clientId={id}
                documents={c.documents || []}
                canEdit={canEdit}
                onPreview={openDoc}
                onChanged={load}
              />
            </Section>
          )}

          {/* Localização — visível apenas para ADMIN/GESTORA */}
          {canEdit && c.latitude != null && c.longitude != null && (            <Section title="Localização do cliente">
              <div className="grid sm:grid-cols-5 gap-5">
                {/* Info textual */}
                <div className="sm:col-span-2 flex flex-col gap-4">
                  <div className="flex items-start gap-3">
                    <div className="w-10 h-10 rounded-md bg-[#D4AF37]/10 border border-[#D4AF37]/30 flex items-center justify-center flex-shrink-0">
                      <MapPin className="text-[#D4AF37]" size={18}/>
                    </div>
                    <div className="min-w-0">
                      <div className="text-[11px] uppercase tracking-wider text-[#737373]">Coordenadas capturadas</div>
                      <div className="font-mono text-sm mt-1" data-testid="client-coords">
                        {Number(c.latitude).toFixed(6)}, {Number(c.longitude).toFixed(6)}
                      </div>
                      <div className="text-[11.5px] text-[#A3A3A3] mt-1">
                        {c.accuracy != null && <>Precisão ±{Math.round(c.accuracy)}m</>}
                        {c.captured_at && (
                          <> · {new Date(c.captured_at).toLocaleString("pt-BR")}</>
                        )}
                      </div>
                    </div>
                  </div>

                  <div className="flex flex-col gap-2">
                    <a
                      href={c.maps_url || `https://www.google.com/maps?q=${c.latitude},${c.longitude}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="fc-btn-gold flex items-center justify-center gap-2"
                      data-testid="btn-view-map"
                    >
                      <MapPin size={15}/> Ver no Google Maps
                      <ExternalLink size={13}/>
                    </a>
                    <a
                      href={`https://www.openstreetmap.org/?mlat=${c.latitude}&mlon=${c.longitude}#map=18/${c.latitude}/${c.longitude}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="fc-btn-ghost text-center text-[12.5px] py-2"
                      data-testid="btn-view-osm"
                    >
                      Abrir no OpenStreetMap
                    </a>
                  </div>
                </div>

                {/* Mini-mapa OpenStreetMap (zero API key) */}
                <div className="sm:col-span-3 rounded-md overflow-hidden border border-[#262626] bg-[#0a0a0a] relative" style={{ minHeight: 260 }}>
                  <iframe
                    title={`Mapa de ${c.full_name || 'cliente'}`}
                    src={`https://www.openstreetmap.org/export/embed.html?bbox=${(c.longitude - 0.003).toFixed(6)},${(c.latitude - 0.002).toFixed(6)},${(c.longitude + 0.003).toFixed(6)},${(c.latitude + 0.002).toFixed(6)}&layer=mapnik&marker=${c.latitude},${c.longitude}`}
                    className="w-full h-[260px] sm:h-full border-0"
                    loading="lazy"
                    referrerPolicy="no-referrer-when-downgrade"
                    data-testid="client-mini-map"
                  />
                </div>
              </div>
            </Section>
          )}
        </div>

        {/* CENTER COLUMN — Painel operacional: Cão Farejador → Contrato → Parcelados → Aprovação Rápida */}
        <div className="lg:col-span-5 space-y-5">
          {/* 🔍 Cão farejador — etapa crítica do fluxo, destaque visual reforçado */}
          {canEdit && (
            <div
              className="relative rounded-md p-[1px]"
              style={{
                background: "linear-gradient(135deg, rgba(212,175,55,0.65) 0%, rgba(212,175,55,0.10) 35%, rgba(212,175,55,0.35) 100%)",
                boxShadow: "0 12px 36px -10px rgba(212,175,55,0.35), 0 0 0 1px rgba(212,175,55,0.10)",
              }}
              data-testid="cao-farejador-wrapper"
            >
              <div
                className="rounded-md"
                style={{
                  background: "linear-gradient(135deg, rgba(212,175,55,0.10) 0%, rgba(40,28,10,0.04) 50%, #0a0a0a 100%)",
                }}
              >
                <AIAnalysisSection clientId={c.id} snapshot={c} onUpdated={load}/>
              </div>
            </div>
          )}

          {/* Contratos — múltiplos permitidos (feature iter45) */}
          {isIndicador ? (
            <ContractSection
              clientId={id}
              canEdit={canEdit}
              isIndicador={isIndicador}
              onContractChange={load}
            />
          ) : (
            <ContractsListSection
              clientId={id}
              canEdit={canEdit}
              isIndicador={isIndicador}
              onContractChange={load}
            />
          )}

          {/* Parcelados e Acordos (operações alternativas) */}
          {!isIndicador && (
            <InstallmentContractsSection clientId={id} canManage={canEdit} />
          )}

          {/* Aprovação rápida / Operação atual — bloco COMPACTO no final */}
          {canEdit && (() => {
            const hasActiveContract = c.status === "contratado";
            return (
              <section className="fc-card p-4 sm:p-5" data-testid="quick-approval-section">
                <div className="flex items-center justify-between mb-3">
                  <h2 className="font-display text-[13px] uppercase tracking-[0.16em] text-[#A3A3A3] flex items-center gap-2">
                    {hasActiveContract ? "Operação atual" : "Aprovação rápida"}
                  </h2>
                  {hasActiveContract ? (
                    <span className="badge-base bg-emerald-500/10 border-emerald-500/40 text-emerald-300 text-[10px]">Contratado</span>
                  ) : (
                    <span className="badge-base bg-[#D4AF37]/10 border-[#D4AF37]/40 text-[#D4AF37] text-[10px]">Pré-contrato</span>
                  )}
                </div>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                  <div className="col-span-1">
                    <label className="fc-label text-[10px]">Status</label>
                    <select className="fc-input py-2 text-[13px]" value={form.status} onChange={e=>setForm({...form, status: e.target.value})} data-testid="select-status">
                      {Object.entries(STATUS_LABELS).map(([v,l])=>(<option key={v} value={v}>{l}</option>))}
                    </select>
                  </div>
                  <div className="col-span-1">
                    <label className="fc-label text-[10px]">Risco</label>
                    <select className="fc-input py-2 text-[13px]" value={form.risk_level || ""} onChange={e=>setForm({...form, risk_level: e.target.value})} data-testid="select-risk">
                      <option value="">—</option>
                      {Object.entries(RISK_LABELS).map(([v,l])=>(<option key={v} value={v}>{l}</option>))}
                    </select>
                  </div>
                  {!hasActiveContract ? (
                    <div className="col-span-2">
                      <label className="fc-label text-[10px]">Valor aprovado (R$)</label>
                      <input className="fc-input py-2 text-[13px]" type="number" value={form.approved_amount ?? ""} onChange={e=>setForm({...form, approved_amount: e.target.value})} data-testid="input-approved_amount"/>
                    </div>
                  ) : (
                    <div className="col-span-2">
                      <label className="fc-label text-[10px]">Próxima ação</label>
                      <input className="fc-input py-2 text-[13px]" value={form.next_action} onChange={e=>setForm({...form, next_action: e.target.value})} data-testid="input-next_action"/>
                    </div>
                  )}
                  {!hasActiveContract && (
                    <div className="col-span-4">
                      <label className="fc-label text-[10px]">Justificativa da análise</label>
                      <textarea rows={2} className="fc-input resize-none text-[13px]" value={form.analysis_justification} onChange={e=>setForm({...form, analysis_justification: e.target.value})} data-testid="input-justification"/>
                    </div>
                  )}
                  <div className="col-span-4">
                    <label className="fc-label text-[10px]">Observações internas</label>
                    <textarea rows={2} className="fc-input resize-none text-[13px]" value={form.internal_notes} onChange={e=>setForm({...form, internal_notes: e.target.value})} data-testid="input-internal_notes"/>
                  </div>
                </div>
                <div className="mt-3 text-[11px] text-[#737373] flex items-start gap-1.5">
                  <span className="text-[#D4AF37]">ⓘ</span>
                  <span>
                    {hasActiveContract
                      ? <>Para alterar valores financeiros, use <strong>Contrato</strong> ou <strong>Parcelados</strong> acima.</>
                      : <>Parcelas, valor da parcela e primeiro vencimento são definidos na <strong>criação do contrato</strong> acima.</>
                    }
                  </span>
                </div>
              </section>
            );
          })()}
        </div>

        {/* RIGHT COLUMN — Mensagens / Contrato assinado / Histórico */}
        <div className="lg:col-span-3 space-y-5">
          {canEdit && (
            <Section title="Mensagens automáticas">
              <div className="space-y-2.5">
                <button onClick={()=>copyToClipboard(summaryWhats)} className="w-full fc-btn-ghost flex items-center gap-2 justify-start text-sm" data-testid="copy-summary">
                  <Copy size={14}/> Copiar resumo WhatsApp
                </button>
                <button onClick={()=>copyToClipboard(clientText)} className="w-full fc-btn-ghost flex items-center gap-2 justify-start text-sm" data-testid="copy-client-text">
                  <MessageSquare size={14}/> Texto para cliente
                </button>
                <button onClick={()=>copyToClipboard(indicatorText)} className="w-full fc-btn-ghost flex items-center gap-2 justify-start text-sm" data-testid="copy-indicator-text">
                  <ShieldAlert size={14}/> Copiar texto p/ indicador
                </button>
                {indicatorWhatsHref ? (
                  <a
                    href={indicatorWhatsHref}
                    target="_blank"
                    rel="noreferrer"
                    className="w-full fc-btn-ghost flex items-center gap-2 justify-start text-sm text-emerald-300 border-emerald-500/20 hover:border-emerald-500/40"
                    data-testid="open-indicator-whatsapp"
                  >
                    <MessageCircle size={14}/> Abrir WhatsApp do indicador
                  </a>
                ) : (
                  <div className="text-[11px] text-[#737373] px-2">Indicador sem WhatsApp cadastrado.</div>
                )}
              </div>
              <div className="mt-4 p-3 rounded-md bg-[#0f0f0f] border border-[#262626]">
                <div className="text-[10px] uppercase tracking-wider text-[#737373] mb-1.5">Pré-visualização (texto p/ indicador)</div>
                <pre className="text-[12px] text-[#A3A3A3] whitespace-pre-wrap font-sans leading-relaxed">{indicatorText}</pre>
              </div>
              <div className="mt-3 text-[11px] text-[#737373]">
                O texto muda automaticamente conforme o status. Salve para refletir alterações.
              </div>
            </Section>
          )}

          {canEdit && (
            <Section title="Contrato assinado" right={<span className="badge-base bg-[#1A1A1A] border-[#D4AF37]/30 text-[#D4AF37]"><FileSignature size={11}/> Upload</span>}>
              <DocCard
                docType="contrato_assinado"
                doc={(c.documents || []).find(d => d.document_type === "contrato_assinado")}
                clientId={id}
                canEdit={canEdit}
                onPreview={openDoc}
                onChanged={load}
              />
              <div className="mt-3 text-[11px] text-[#737373]">
                Faça o upload do contrato assinado pelo cliente. Aceita imagem e PDF.
              </div>
            </Section>
          )}

          {!isIndicador && (
            <Section title="Histórico">
              <ul className="space-y-3">
                {c.history?.length === 0 && <li className="text-sm text-[#737373]">Sem alterações registradas.</li>}
                {c.history?.map(h => (
                  <li key={h.id} className="flex gap-3 text-[13px]">
                    <div className="w-1.5 h-1.5 rounded-full bg-[#D4AF37] mt-1.5 flex-shrink-0"/>
                    <div>
                      <div className="text-white">
                        <span className="text-[#A3A3A3]">{h.old_status ? STATUS_LABELS[h.old_status] : "—"}</span>
                        <span className="mx-1.5 text-[#737373]">→</span>
                        <span className="text-[#D4AF37]">{STATUS_LABELS[h.new_status]}</span>
                      </div>
                      <div className="text-[11px] text-[#737373] mt-0.5">{formatDateTimeBR(h.created_at)} · {h.changed_by}</div>
                      {h.note && <div className="text-[12px] text-[#A3A3A3] mt-1">{h.note}</div>}
                    </div>
                  </li>
                ))}
              </ul>
            </Section>
          )}

          {!isIndicador && (
            <ClientCommunicationCard client={c} />
          )}

          {!isIndicador && (
            <BlockSection client={c} canBlock={canEdit} canUnblock={user?.role === "admin"} onChanged={load}/>
          )}

          {canDelete && (
            <button onClick={() => setConfirmDelete(true)} className="w-full text-[12px] text-red-400 hover:text-red-300 py-3 px-4 border border-red-500/20 rounded-md bg-red-500/5" data-testid="btn-delete-client">
              Excluir cliente permanentemente
            </button>
          )}
        </div>
      </div>

      {/* Confirm delete modal */}
      {confirmDelete && (
        <div
          className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-4"
          onClick={closeDelete}
          data-testid="delete-confirm-modal"
        >
          <div
            className="fc-card w-full max-w-md p-5 sm:p-6 border border-red-500/30"
            onClick={e => e.stopPropagation()}
          >
            <div className="flex items-start gap-3">
              <div className="w-10 h-10 rounded-md bg-red-500/10 border border-red-500/30 flex items-center justify-center flex-shrink-0">
                <ShieldAlert className="text-red-400" size={20}/>
              </div>
              <div className="min-w-0">
                <div className="text-[10px] uppercase tracking-[0.2em] text-red-300">Ação irreversível</div>
                <h3 className="font-display text-xl font-bold mt-0.5">Excluir cliente permanentemente</h3>
              </div>
            </div>
            <div className="mt-4 text-[13px] text-[#A3A3A3] leading-relaxed">
              Esta ação remove o cliente <span className="text-white font-medium">{c.full_name}</span>, todos os documentos, análises de IA, localização, histórico de status e contratos sem movimentação financeira.
              <div className="mt-2 text-[12px] text-amber-300/90">
                Se houver contrato ativo ou pagamento já lançado, a exclusão será bloqueada.
              </div>
            </div>
            <div className="mt-4">
              <label className="fc-label">Para confirmar, digite <span className="text-red-300 font-semibold">EXCLUIR</span></label>
              <input
                autoFocus
                value={deleteText}
                onChange={e => setDeleteText(e.target.value)}
                className="fc-input mt-1.5 tracking-widest"
                placeholder="EXCLUIR"
                data-testid="delete-confirm-input"
                disabled={deleting}
              />
            </div>
            <div className="flex items-center justify-end gap-2 mt-5">
              <button
                type="button"
                onClick={closeDelete}
                disabled={deleting}
                className="fc-btn-ghost text-sm px-4 py-2 disabled:opacity-50"
                data-testid="delete-cancel"
              >
                Cancelar
              </button>
              <button
                type="button"
                onClick={remove}
                disabled={deleting || deleteText.trim().toUpperCase() !== "EXCLUIR"}
                className="text-sm px-4 py-2 rounded-md font-medium border border-red-500/50 bg-red-500/15 text-red-300 hover:bg-red-500/25 hover:text-red-200 disabled:opacity-40 disabled:cursor-not-allowed flex items-center gap-2"
                data-testid="delete-confirm-button"
              >
                {deleting && <Loader2 size={14} className="animate-spin"/>}
                {deleting ? "Excluindo..." : "Excluir cliente"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Document preview modal */}
      {preview && (
        <div className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-4" onClick={() => { URL.revokeObjectURL(preview.url); setPreview(null); }} data-testid="doc-preview-modal">
          <div className="max-w-4xl w-full max-h-[90vh] fc-card overflow-hidden" onClick={e=>e.stopPropagation()}>
            <div className="flex items-center justify-between px-5 py-3 border-b border-[#262626]">
              <div className="font-medium">{preview.label}</div>
              <button onClick={() => { URL.revokeObjectURL(preview.url); setPreview(null); }} className="text-[#A3A3A3] hover:text-white text-sm">Fechar</button>
            </div>
            <div className="p-3 bg-black flex items-center justify-center" style={{minHeight: "60vh"}}>
              {preview.type?.startsWith("image/") ? (
                <img src={preview.url} alt="" className="max-h-[75vh] max-w-full object-contain"/>
              ) : (
                <iframe src={preview.url} title="Documento" className="w-full h-[75vh] bg-white rounded"/>
              )}
            </div>
          </div>
        </div>
      )}

      <SendWhatsAppModal
        open={waOpen}
        onClose={() => setWaOpen(false)}
        clients={[{ id: c.id, full_name: c.full_name, whatsapp: c.whatsapp }]}
      />
      <ChangeIndicatorModal
        open={changeIndOpen}
        onClose={() => setChangeIndOpen(false)}
        client={{ id: c.id, full_name: c.full_name, indicator_id: c.indicator_id, indicator_name: c.indicator_name }}
        onChanged={load}
      />
    </Layout>
  );
}
